import { Box } from '@mui/material';
import { Dayjs } from 'dayjs';
import { useRef, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { SnackbarSeverityEnum } from '../../../enums/snackbar-severity-enum';
import { IUserFileDB } from '../../../interfaces/file.interface';
import { ISportBackgroundFormData } from '../../../interfaces/user-sport-background.interface';
import {
  IWorkExperience,
  IWorkExperienceFormData,
} from '../../../interfaces/user-work-experience.interface';
import { CompleteOnboardingRequest } from '../../../interfaces/onboarding.interface';
import { useCompleteOnboardingMutation } from '../../../store/apiSlice/userApi';
import { errorHelper } from '../../../utils/helper/error-helper';
import {
  createSportBackgroundSchema,
  createTeamSchema,
} from '../../../validation/sport-validation';
import CustomStepper from '../../MUIComponents/CustomStepper';
import SnackbarCustom from '../../MUIComponents/SnackbarCustom';
import UploadDocument from '../../shared/UploadDocumentForm/UploadDocument';
import PersonalInformationForm from './PersonalInformationForm/PersonalInformationForm';
import OnboardingSportBackgroundStep from './OnboardingSportBackground/OnboardingSportBackgroundStep';
import WorkExperience from './WorkExperience/WorkExperience';
import { isSupportedIdConfirmationFileHelper } from '../../../utils/helper/file-validation-helper';
import { OTHER_SPORT } from '../../../constants';
import { UserRoleEnum } from '../../../enums/user-data-field-enum';

type IFormData = Partial<
  RegistrationFormInputs &
    FileUploadFormInputs &
    ISportBackgroundFormData &
    IWorkExperienceFormData
>;

interface RegistrationFormInputs {
  firstName: string;
  lastName: string;
  birthdate: Dayjs;
  isBirthdateVisible?: boolean;
}

interface FileUploadFormInputs {
  documents: File[];
}

interface IVerifyAccountProps {
  role?: UserRoleEnum;
}

const VerifyAccount = (props: IVerifyAccountProps) => {
  const { role } = props;
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isFinalizingRef = useRef(false);
  const [formData, setFormData] = useState<IFormData>({
    isBirthdateVisible: true,
  });
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<IUserFileDB[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarSeverityEnum>(SnackbarSeverityEnum.ERROR);
  const navigate = useNavigate();

  const updateFormData = (partial: Partial<IFormData>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const [completeOnboarding] = useCompleteOnboardingMutation();

  const onSubmitUser: SubmitHandler<RegistrationFormInputs> = (data) => {
    updateFormData(data);
    setStep(step + 1);
  };
  const onSubmitFiles: SubmitHandler<FileUploadFormInputs> = (data) => {
    updateFormData(data);
    setStep(step + 1);
  };
  const onSubmitSport: SubmitHandler<ISportBackgroundFormData> = async (
    data
  ) => {
    const sportBackgrounds = data.sportBackgrounds;

    if (role === UserRoleEnum.USER) {
      try {
        // Ensure each sport background has a selected sport
        for (const sportBackground of sportBackgrounds) {
          if (
            !sportBackground.sportId ||
            sportBackground.sportId.trim().length === 0
          ) {
            throw new Error('Sport is required for each sport background.');
          }
        }

        // Handle check sportBackgrounds
        for (const sportBackground of sportBackgrounds) {
          try {
            await createSportBackgroundSchema(
              sportBackground.isOtherSport ?? false
            ).validate(sportBackground, {
              stripUnknown: true,
              abortEarly: false,
            });
          } catch (validationErrors: any) {
            const errorMessages: any[] = validationErrors.inner.map(
              (error: any) => ({
                path: error.path,
                message: error.message,
              })
            );
            const errorToString = errorMessages.reduce<string>(
              (acc, cur) => acc + `${cur.message}\n`,
              ''
            );
            throw new Error(errorToString);
          }

          // Handle check teams at a sport background
          for (const team of sportBackground.teams || []) {
            try {
              await createTeamSchema(
                sportBackground.isOtherSport ?? false
              ).validate(team, {
                abortEarly: false,
              });
            } catch (validationErrors: any) {
              const errorMessages: any[] = validationErrors.inner.map(
                (error: any) => ({
                  path: error.path,
                  message: error.message,
                })
              );
              const errorToString = errorMessages.reduce<string>(
                (acc, cur) => acc + `${cur.message}\n`,
                ''
              );
              throw new Error(errorToString);
            }
          }
        }
        // eslint-disable-next-line
      } catch (error) {
        setIsSubmitting(false);
        setSnackbarMessage('Please fix the highlighted fields');
        setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
        setSnackbarOpen(true);
        return;
      }
      const sportIds = [] as string[];

      const uniqueSportIds = new Set();

      sportBackgrounds.forEach((sportBackground) => {
        if (!uniqueSportIds.has(sportBackground.sportId)) {
          uniqueSportIds.add(sportBackground.sportId);
          if (sportBackground.sportId.length > 0) {
            sportIds.push(sportBackground.sportId);
          }
        }
      });

      if (sportIds.length === 0) {
        setSnackbarMessage('Please select at least one sport.');
        setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
        setSnackbarOpen(true);
        return;
      }
    }
    updateFormData(data);
    setStep(step + 1);
  };

  const handleFinalizeVerification: SubmitHandler<
    IWorkExperienceFormData
  > = async (data) => {
    if (isFinalizingRef.current) return;
    isFinalizingRef.current = true;

    const workExperiences = data.workExperiences;

    updateFormData({ workExperiences: workExperiences as any });

    setIsSubmitting(true);

    const sportBackgrounds = (formData as ISportBackgroundFormData)
      .sportBackgrounds;
    const userRegistrationData = formData as RegistrationFormInputs;

    if (files.length === 0) {
      setIsSubmitting(false);
      setSnackbarMessage('Please upload at least one ID document.');
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
      isFinalizingRef.current = false;
      return;
    }

    try {
      if (role === UserRoleEnum.USER) {
        if (!sportBackgrounds) {
          throw Error('Sport background(s) are required for athletes.');
        }
      }
      const attachmentFileMap: Record<string, File> = {};

      const onboardingPayload: CompleteOnboardingRequest = {
        personalInfo: {
          firstName: userRegistrationData.firstName,
          lastName: userRegistrationData.lastName,
          birthdate: userRegistrationData.birthdate?.toISOString() || '',
          isBirthdateVisible: userRegistrationData.isBirthdateVisible,
        },
        sportBackgrounds:
          role === UserRoleEnum.USER && sportBackgrounds
            ? sportBackgrounds.map((sportBackground, sbIndex) => {
                const isOtherSport = sportBackground.sportId === OTHER_SPORT.id;

                if (!sportBackground.startCareerDate) {
                  throw new Error(
                    'Start career date is required for each sport.'
                  );
                }

                const startCareerDate =
                  sportBackground.startCareerDate.toISOString();
                const endCareerDate = sportBackground.isActiveSport
                  ? undefined
                  : sportBackground.endCareerDate?.toISOString();

                const attachments = (sportBackground.fileData || []).map(
                  (fileData, fileIndex) => {
                    const clientKey = `sb-${sbIndex}-file-${fileIndex}-${fileData.file.name}`;
                    attachmentFileMap[clientKey] = fileData.file;
                    return {
                      clientKey,
                      description: fileData.description || undefined,
                      links: fileData.links || [],
                    };
                  }
                );

                return {
                  sportId: isOtherSport ? undefined : sportBackground.sportId,
                  otherSportName: isOtherSport
                    ? sportBackground.sportName || undefined
                    : undefined,
                  isIndividualSport: !!sportBackground.isIndividualSport,
                  isActiveSport: !!sportBackground.isActiveSport,
                  startCareerDate,
                  endCareerDate,
                  description: sportBackground.description || undefined,
                  attachments,
                  teams: (sportBackground.teams || []).map((team) => ({
                    leagueId: team.leagueId || undefined,
                    leagueName: team.leagueName || '',
                    teamId: team.teamId || undefined,
                    teamName: team.teamName || undefined,
                    startDate: team.startDateInTeam.toISOString(),
                    endDate: team.isActiveTeam
                      ? undefined
                      : team.endDateInTeam?.toISOString(),
                    activeCareer: !!team.isActiveTeam,
                  })),
                };
              })
            : [],
        workExperiences: (workExperiences || []).map((we) => ({
          ...we,
        })),
      };

      const formData = new FormData();
      formData.append('payload', JSON.stringify(onboardingPayload));

      files.forEach((file) => {
        formData.append('idDocuments', file);
      });

      Object.values(attachmentFileMap).forEach((file) => {
        formData.append('sportAttachments', file);
      });

      await completeOnboarding(formData).unwrap();
      setSnackbarMessage('Profile verification successful.');
      setSnackbarSeverity(SnackbarSeverityEnum.SUCCESS);

      if (role === UserRoleEnum.AGENT) {
        navigate('/');
      }

      navigate('/welcome-to-platform', { replace: true });
    } catch (error: any) {
      const errorMessage = errorHelper(error);
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
      isFinalizingRef.current = false;
    }
  };

  const steps =
    role && role === UserRoleEnum.AGENT
      ? ['Personal Information', 'ID Confirmation']
      : [
          'Personal Information',
          'ID Confirmation',
          'Sport Background',
          'Work experience',
        ];

  return (
    <Box
      display={'flex'}
      maxWidth={'875px'}
      margin={'0 auto'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'center'}
      marginBottom={'64px'}
    >
      <CustomStepper steps={steps} step={step} />

      {step === 1 && (
        <PersonalInformationForm
          onSubmitUser={onSubmitUser}
          formData={formData as RegistrationFormInputs}
          setStep={setStep}
          step={step}
        />
      )}
      {step === 2 && (
        <UploadDocument
          title={'ID Confirmation'}
          contentText={
            <>
              To complete your registration, please upload a valid ID document
              or
              <br />
              driver’s license for verification.
              <br />
              This ensures the authenticity of your profile.
              <br />
              <br />
              <strong>Why do we need your ID?</strong>
              <br />
              To keep Free Agent exclusive and secure, we verify every user.
              <br />
              Uploading your ID helps us confirm your identity and athletic
              background.
              <br />
              Your info is encrypted, never shared, and used{' '}
              <strong>only for verification purposes</strong>.
            </>
          }
          dragAndDropContentText={
            <>
              Drag and drop your ID document or driver’s license here to
              complete your registration.
              <br />
              Ensure the document is clear and legible for verification
              purposes.
              <br />
              You can upload up to 5 files. Only images (JPEG, PNG, JPG, WebP)
              are accepted.
            </>
          }
          onSubmitFiles={
            role === UserRoleEnum.AGENT ? onSubmitSport : onSubmitFiles
          }
          formData={formData as FileUploadFormInputs}
          setStep={setStep}
          step={step}
          files={files}
          setFiles={setFiles}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          verifyFileHelper={isSupportedIdConfirmationFileHelper}
          isLoading={false}
          isError={false}
          error={undefined}
          responseData={undefined}
        />
      )}
      {step === 3 && (
        <OnboardingSportBackgroundStep
          onSubmitSport={onSubmitSport}
          setStep={setStep}
          step={step}
          isLoading={isSubmitting}
          savedSportBackgrounds={
            (formData as ISportBackgroundFormData).sportBackgrounds
          }
          onInvalid={() => {
            setSnackbarMessage('Please fix the highlighted fields');
            setSnackbarSeverity(SnackbarSeverityEnum.ERROR);
            setSnackbarOpen(true);
          }}
        />
      )}
      {step === 4 && (
        <WorkExperience
          onSubmitWorkExperience={handleFinalizeVerification}
          setStep={setStep}
          step={step}
          isLoading={isSubmitting}
          savedWorkExperiences={
            (formData as any).workExperiences as IWorkExperience[] | undefined
          }
        />
      )}
      <SnackbarCustom
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
};

export default VerifyAccount;
